import { seenModalIdsAtom } from '@/atoms/modalQueueAtoms';
import { reactQueryClient } from '@/utils/reactQueryClient';
import {
  HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID,
  HEIDI_2_EXPLORE_LATER_BANNER_MODAL_ID,
  HEIDI_2_GET_STARTED_CHECKLIST_MODAL_ID,
} from '@heidi/agents/contracts';
import {
  heidi2ExploreLaterPreviewAtom,
  heidi2ExploreLaterPreviewStartedAtAtom,
} from '@heidi/agents/data';
import { type ModalId } from '@heidi/schema/ml-scribe';
import { parseIsoMaybe } from '@heidi/utils/dates';
import { GET_USER, type UserResponse } from '@heidi/workspace/contracts';
import { type QueryClient } from '@tanstack/react-query';
import { type createStore, getDefaultStore } from 'jotai';

/**
 * The three nudges a preview replays. Closes of these are saved for real, so a
 * user refetch or a mark-as-viewed response brings an earlier close back and
 * the banner vanishes. Anything recorded before the preview started is dropped
 * from the cached user and the queue's seen set; a close made during the
 * preview is left alone.
 */
const PREVIEW_NUDGE_IDS: readonly ModalId[] = [
  HEIDI_2_EXPLORE_LATER_BANNER_MODAL_ID,
  HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID,
  HEIDI_2_GET_STARTED_CHECKLIST_MODAL_ID,
];

type JotaiStore = ReturnType<typeof createStore>;

const closesRecordedBeforePreview = (
  user: UserResponse | undefined,
  previewStartedAt: string
): ModalId[] => {
  const startedAt = parseIsoMaybe(previewStartedAt)?.getTime();
  const seenModals = user?.seen_modals;
  if (startedAt === undefined || !seenModals) return [];

  return PREVIEW_NUDGE_IDS.filter((id) => {
    const lastSeenAt = parseIsoMaybe(seenModals[id]?.last_seen_at)?.getTime();
    return lastSeenAt !== undefined && lastSeenAt < startedAt;
  });
};

let reconciling = false;

/** Drop pre-preview closes from the cache and the seen set. No-op otherwise. */
export const reconcileExploreLaterPreviewSeenState = (
  queryClient: QueryClient,
  store: JotaiStore
): void => {
  if (reconciling) return;
  if (store.get(heidi2ExploreLaterPreviewAtom) === null) return;
  const previewStartedAt = store.get(heidi2ExploreLaterPreviewStartedAtAtom);
  if (!previewStartedAt) return;

  const user = queryClient.getQueryData<UserResponse>([GET_USER]);
  const staleIds = closesRecordedBeforePreview(user, previewStartedAt);
  if (staleIds.length === 0 || !user?.seen_modals) return;

  const seenModals = { ...user.seen_modals };
  for (const id of staleIds) {
    delete seenModals[id];
  }

  reconciling = true;
  try {
    queryClient.setQueryData<UserResponse>([GET_USER], {
      ...user,
      seen_modals: seenModals,
    });
    store.set(seenModalIdsAtom, (prev) => {
      if (!prev || staleIds.every((id) => !prev.has(id))) return prev;
      const next = new Set(prev);
      for (const id of staleIds) next.delete(id);
      return next;
    });
  } finally {
    reconciling = false;
  }
};

/**
 * A refetch writes the account's old closes back after the preview has cleared
 * them. Re-apply the drop on every user-cache write, and once up front for a
 * preview already running when dev tools mount.
 */
export const subscribeExploreLaterPreviewSeenGuard = (
  queryClient: QueryClient = reactQueryClient,
  store: JotaiStore = getDefaultStore()
): (() => void) => {
  reconcileExploreLaterPreviewSeenState(queryClient, store);
  return queryClient.getQueryCache().subscribe((event) => {
    const queryKey = event.query.queryKey as readonly unknown[];
    if (event.type !== 'updated' || queryKey[0] !== GET_USER) return;
    reconcileExploreLaterPreviewSeenState(queryClient, store);
  });
};
